// Pure helpers + constants for the double-click column auto-fit behaviour.
// The DOM measurement and the synthetic-resize wiring live in `useColumnAutofit.ts`;
// everything here is side-effect-free so it can be unit tested without a layout engine.

/** Upper bound an auto-fit can grow a column to (the "center max default"). */
export const AUTOFIT_MAX_WIDTH = 400;

/** Lower bound an auto-fit can shrink a column to. */
export const AUTOFIT_MIN_WIDTH = 60;

/**
 * How close (px) a double-click must be to a column's right border to count as
 * targeting the resize handle rather than the header body (which would sort).
 */
export const AUTOFIT_EDGE_TOLERANCE = 6;

/** Extra room added to the header's natural width for the sort caret + resize handle. */
export const AUTOFIT_HEADER_PADDING = 20;

/** Small breathing room added to the widest measured content before clamping. */
export const AUTOFIT_CELL_SAFETY = 8;

/** Clamp a measured natural width into the allowed auto-fit range. */
export const clampAutofitWidth = (
  natural: number,
  minWidth: number = AUTOFIT_MIN_WIDTH,
  maxWidth: number = AUTOFIT_MAX_WIDTH
): number => Math.min(maxWidth, Math.max(minWidth, Math.round(natural)));

/** True when `pointerX` is within `tolerance` px of a column's right border. */
export const isWithinColumnEdge = (
  pointerX: number,
  columnRight: number,
  tolerance: number = AUTOFIT_EDGE_TOLERANCE
): boolean => Math.abs(pointerX - columnRight) <= tolerance;

export interface ComputeAutofitWidthOptions {
  minWidth?: number;
  maxWidth?: number;
  headerPadding?: number;
  cellSafety?: number;
}

/**
 * Compute the fitted column width from the header's natural width and the natural
 * widths of the (currently loaded) body cells. The widest of header-plus-handle and
 * the widest cell wins, a safety margin is added, then the result is clamped.
 */
export const computeAutofitWidth = (
  headerNatural: number,
  cellNaturals: number[],
  options: ComputeAutofitWidthOptions = {}
): number => {
  const {
    minWidth = AUTOFIT_MIN_WIDTH,
    maxWidth = AUTOFIT_MAX_WIDTH,
    headerPadding = AUTOFIT_HEADER_PADDING,
    cellSafety = AUTOFIT_CELL_SAFETY,
  } = options;

  const widestCell = cellNaturals.length > 0 ? Math.max(...cellNaturals) : 0;
  const widest = Math.max(headerNatural + headerPadding, widestCell);

  return clampAutofitWidth(widest + cellSafety, minWidth, maxWidth);
};
